// @file Composable that provides methods for draggable posts
import { isSingleSelectPropertyRequiredAndPostMovingToNoValueGroup } from '@@/bits/post_drag_and_drop'
import { getCustomPropertyValue, shouldPostsBeDeletedAfterRemovingCustomProperty } from '@@/bits/post_properties'
import { isPostPinned } from '@@/bits/post_state'
import { WallWishGroupByValue } from '@@/enums'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostGroupByStore } from '@@/pinia/surface_post_group_by_store'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { LogicalEdge } from '@@/pragmatic-drag-and-drop/helpers'
import type { Post, WallGroupPost } from '@@/types'
import { storeToRefs } from 'pinia'

export const useSurfacePostDraggableHelper = (): {
  canDropPost: typeof canDropPost
  getEdgeForDropTargetPost: typeof getEdgeForDropTargetPost
} => {
  const {
    isSectionedAndNonmanualSorted,
    isGroupByNone,
    isGroupByAuthor,
    isGroupBySection,
    isGroupByColor,
    isGroupBySingleSelect,
    customPropIdFromWishGroupBy,
  } = storeToRefs(useSurfaceWishArrangementStore())
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const { allSortedPostsByGroup } = storeToRefs(surfacePostsStore)
  const { groupHasPinnedPosts, isGroupHasUnpinnedPosts } = surfacePostsStore
  const { currentSingleSelectProperty } = storeToRefs(useSurfacePostGroupByStore())

  function isDraggedToTopOfGroup(targetPost: Post, targetGroupId: WallGroupPost['id']): boolean {
    const posts = allSortedPostsByGroup.value[targetGroupId]
    if (posts === undefined) return true
    if (posts.length === 0) return true
    return posts[0].cid === targetPost.cid
  }
  function isDraggedToBottomOfGroup(targetPost: Post, targetGroupId: WallGroupPost['id']): boolean {
    const posts = allSortedPostsByGroup.value[targetGroupId]
    if (posts === undefined) return true
    if (posts.length === 0) return true
    return posts[posts.length - 1].cid === targetPost.cid
  }

  function canDropPost(draggedPost: Post, targetPost: Post): boolean {
    // if we aren't sorting manually, don't allow D&D operations
    if (isSectionedAndNonmanualSorted.value) {
      return false
    }
    const isDraggedPostPinned = isPostPinned(draggedPost)
    const isTargetPostPinned = isPostPinned(targetPost)

    if (isGroupByNone.value) {
      // For none group, pinned posts can only be dragged to pinned posts. and same for unpinned posts
      if (isDraggedPostPinned && !isTargetPostPinned) {
        return false
      }
      if (!isDraggedPostPinned && isTargetPostPinned) {
        return false
      }
      return true
    }

    // If group by author, posts can only be moved within the same author group
    if (isGroupByAuthor.value) {
      // Posts must belong to the same author
      if (draggedPost.author_id !== targetPost.author_id) {
        return false
      }
      // Pinned posts can only be dragged to pinned posts, and unpinned to unpinned
      if (isDraggedPostPinned && !isTargetPostPinned) {
        return false
      }
      if (!isDraggedPostPinned && isTargetPostPinned) {
        return false
      }
      return true
    }

    // If group by single select, posts can only be moved to 'No value' group if that single select property is not required property
    if (
      isSingleSelectPropertyRequiredAndPostMovingToNoValueGroup(
        currentSingleSelectProperty.value,
        draggedPost,
        targetPost,
      )
    ) {
      return false
    }

    const isPostMovingToNoValueGroupAndWillBeEmpty =
      isGroupBySingleSelect.value &&
      customPropIdFromWishGroupBy.value != null &&
      targetPost.custom_properties?.[customPropIdFromWishGroupBy.value] == null &&
      shouldPostsBeDeletedAfterRemovingCustomProperty(customPropIdFromWishGroupBy.value, [draggedPost])
    if (isPostMovingToNoValueGroupAndWillBeEmpty) {
      return false
    }

    // related element is a post -> we can drag there conditionally
    const targetGroupId: WallGroupPost['id'] | null = (() => {
      if (isGroupBySection.value) {
        return surfaceSectionsStore.sectionEntities[targetPost.wall_section_id ?? -1]?.id ?? null
      } else if (isGroupByAuthor.value) {
        return targetPost.author_id ?? null
      } else if (isGroupByColor.value) {
        return targetPost.color ?? 'default'
      } else if (isGroupBySingleSelect.value) {
        if (currentSingleSelectProperty.value?.id == null) return null
        const value = getCustomPropertyValue(targetPost, currentSingleSelectProperty.value)
        return typeof value === 'string' ? value : WallWishGroupByValue.NoValue
      }
      return null
    })()
    if (targetGroupId == null) {
      return false
    }
    // if dragged is pinned and incoming group only has unpinned posts and we are dragging to the top of the group, D&D is OK
    if (
      isDraggedPostPinned &&
      !groupHasPinnedPosts(targetGroupId) &&
      isDraggedToTopOfGroup(targetPost, targetGroupId)
    ) {
      return true
    }
    // conversely, if post is unpinned and incoming group is entirely pinned, and we are dragging to the bottom, D&D is OK
    if (
      !isDraggedPostPinned &&
      !isGroupHasUnpinnedPosts(targetGroupId) &&
      isDraggedToBottomOfGroup(targetPost, targetGroupId)
    ) {
      return true
    }
    // otherwise, pinned posts can only be dragged to pinned posts. and same for unpinned posts
    if (isDraggedPostPinned && !isTargetPostPinned) {
      return false
    }
    if (!isDraggedPostPinned && isTargetPostPinned) {
      return false
    }
    return true
  }

  function getEdgeForDropTargetPost(
    dropTargetPost: Post,
    sourcePost: Post,
    axis: 'vertical' | 'horizontal',
  ): LogicalEdge | undefined {
    const isDropTargetPostPinned = isPostPinned(dropTargetPost)
    const isDraggedPostPinned = isPostPinned(sourcePost)
    // If post being dragged is bit pinned and the drop target post is, we can only drop
    // the dragged post below of the drop target post
    if (!isDraggedPostPinned && isDropTargetPostPinned) return axis === 'vertical' ? 'bottom' : 'end'
    // If post being dragged is pinned and the drop target post is not, we can only drop
    // the dragged post on top of the drop target post
    if (isDraggedPostPinned && !isDropTargetPostPinned) return axis === 'vertical' ? 'top' : 'start'
    return undefined
  }

  return {
    canDropPost,
    getEdgeForDropTargetPost,
  }
}
